import { FunctionComponent, cloneElement, useEffect, useState } from 'react';
import { flatten, groupBy, isEmpty, mapValues, values } from 'es-toolkit/compat';
import clsx from 'clsx';

import { Button, TITLE_TEXT, TockDialog } from 'omakase';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { IconChevronLeft } from '@tock/icons';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { appRequestInProgressSelector } from 'selectors/appSelector';
import { consumer } from '@tock/coretypes';
import {
  patronOpenModalSelector,
  patronQuizAnswersSelector,
  patronQuizResultsSelector,
} from 'selectors/patronSelectors';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import QuizModalResults from './QuizModalResults';
import QuizQuestion from 'components/profile/quiz/QuizQuestion';

type AnswerOptions = NumericDictionary<number[]>;

interface TransitionsToClassNames {
  PREV: 'prev';
  NEXT: 'next';
  SUBMIT: 'submit';
}

const transitionClassNameMap: TransitionsToClassNames = {
  PREV: 'prev',
  NEXT: 'next',
  SUBMIT: 'submit',
};

const QuizModal: FunctionComponent<{}> = () => {
  const { existingAnswers, isOpen, isLoading, questions, quizResults } =
    useSelector(mapStateToProps);

  const [questionIdx, setQuestionIdx] = useState<number>(0);
  const [transition, setTransition] = useState<keyof TransitionsToClassNames>();
  const [answers, setAnswers] = useState<AnswerOptions>({});

  const {
    actions: { patron: patronActions },
  } = useActions();

  useMountEffect(() => {
    patronActions.fetchQuizAnswers();
    patronActions.fetchQuizQuestions();
  });

  useEffect(() => {
    const existingAnswerOptions: Dictionary<consumer.PatronQuizAnswer[]> = groupBy(
      existingAnswers,
      'questionId'
    );
    const initialQuestionIdToAnswer = mapValues(
      existingAnswerOptions,
      (answerOptions: consumer.PatronQuizAnswer[]) =>
        answerOptions.map((option) => option.optionId) || [0]
    );
    setAnswers(initialQuestionIdToAnswer);
  }, [existingAnswers]);

  const handleClose = () => {
    patronActions.closeQuizModal();

    setQuestionIdx(0);
    setAnswers({});
  };

  const handleSubmit = () => {
    if (answers) {
      setTransition('SUBMIT');
      patronActions.submitQuizResponses(flatten(values(answers)), { hideToast: true }).then(() => {
        patronActions.fetchQuizResults();
        handleNext();
      });
      if (window.analytics) {
        window.analytics.sendEvent('button', 'click', 'Complete Personality Quiz', null, {
          transport: 'beacon',
        });
      }
    }
  };

  const handleBack = () => {
    setQuestionIdx(questionIdx - 1);
    if (questions && questionIdx < questions.length) {
      setTransition('PREV');
    }
  };

  const handleNext = () => {
    setQuestionIdx(questionIdx + 1);
    if (questions && questionIdx + 1 < questions.length) {
      setTransition('NEXT');
    }
  };

  const modalClassName = clsx('QuizModal', {
    'is-results': questionIdx === questions?.length,
  });

  const questionComponents =
    questions?.map((question) => {
      const questionId = question.id;
      return (
        <div>
          <QuizQuestion
            question={question}
            onAnswer={(optionId: number[]) => {
              const newAnswers = { ...answers, [questionId]: optionId };
              if (isEmpty(optionId)) {
                delete newAnswers[questionId];
              }
              setAnswers(newAnswers);
            }}
            selectedOptionId={answers && answers[questionId]}
          />
        </div>
      );
    }) || [];

  const questionId = questions && questions[questionIdx] && questions[questionIdx].id;

  const quizBody = () => {
    const questionComponent = questionComponents[questionIdx];
    if (questionComponent) {
      return questionComponent;
    } else if (questionIdx === questions?.length) {
      return <QuizModalResults results={quizResults} />;
    }
    return null;
  };

  const footer = (
    <>
      {questionIdx > 0 ? (
        <Button
          fullWidth
          iconStart={<IconChevronLeft />}
          size="large"
          variant="basic"
          onClick={handleBack}
        >
          Back
        </Button>
      ) : undefined}
      {(() => {
        const lastQuestionIdx = (questions?.length || 0) - 1;
        if (questionIdx < lastQuestionIdx) {
          const currentQuestion = questions && questions[questionIdx];
          const currentQuestionId = currentQuestion?.id;
          return (
            <Button
              disabled={!!currentQuestionId && isEmpty(answers[currentQuestionId])}
              size="large"
              variant="primary"
              onClick={handleNext}
              fullWidth
            >
              Next
            </Button>
          );
        } else if (questionIdx === lastQuestionIdx) {
          return (
            <Button
              disabled={values(answers).length !== questions?.length}
              size="large"
              variant="primary"
              onClick={handleSubmit}
              fullWidth
            >
              Submit
            </Button>
          );
        } else {
          return (
            <Button size="large" variant="primary" onClick={handleClose} fullWidth>
              Done
            </Button>
          );
        }
      })()}
    </>
  );

  return (
    <TockDialog
      aria-labelledby={TITLE_TEXT}
      headingConfig={{
        left: {
          configType: 'title',
          title: 'Your culinary personality',
        },
        right: {
          configType: 'closeButton',
        },
      }}
      onClose={handleClose}
      open={isOpen}
      type="fullScreen"
      actionsContent={footer}
      fullHeight
      showShadowWhenScrolled
    >
      <TransitionGroup
        className="QuizModal-transitionGroup"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        childFactory={(child: any) => {
          const transitionClassName = transition && transitionClassNameMap[transition];
          return cloneElement(child, {
            classNames: clsx(child.props.className, transitionClassName),
          });
        }}
      >
        <CSSTransition key={`question-${questionId}`} timeout={{ enter: 3500, exit: 100 }}>
          <div className={modalClassName}>
            <LoadingComponent
              isLoading={isLoading}
              containerClassName="QuizModal-loading"
              isBallPulse
            >
              <div className="QuizModal-body">{quizBody()}</div>
            </LoadingComponent>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </TockDialog>
  );
};

function mapStateToProps(state: StoreType) {
  return {
    isOpen: patronOpenModalSelector(state) === 'QUIZ',
    isLoading: appRequestInProgressSelector(state),
    questions: state.patron.quizQuestions,
    existingAnswers: patronQuizAnswersSelector(state),
    quizResults: patronQuizResultsSelector(state),
  };
}

export default QuizModal;
